import React from 'react';
import classnames from 'classnames';
import { Button, RadioButton, TextInput, Loader } from 'is24-corecss';
import { useForm } from 'react-hook-form';
import { UserSalutation } from '../../../../../../@types/userAccount';
import styles from './styles';
import type { ProfileBenefitsUserData } from '../../../../../../@types/productBenefits';
import { useGetProductBenefitsPersonalData } from '../../../../../../apiHooks/productBenefits.hook';
import { Msg, useIntl } from '@/i18n';
import { validateSalutation } from '@/utils/validateSalutation.util';

interface Props {
  close: () => void;
  onSubmit: (values: Omit<ProfileBenefitsUserData, 'policyNumber'>) => Promise<void>;
  isLoading: boolean;
}

const MietrechtsberatungSubmitForm: React.FC<Props> = ({ close, onSubmit, isLoading }) => {
  const { t } = useIntl();
  const { personalData: profile } = useGetProductBenefitsPersonalData();
  const defaultValues = {
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    city: profile?.city || '',
    houseNumber: profile?.houseNumber || '',
    street: profile?.street || '',
    postcode: profile?.postcode || '',
    salutation: profile?.salutation || UserSalutation.FEMALE,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues, values: defaultValues, mode: 'all', criteriaMode: 'all' });

  return (
    <form className={styles.container} onSubmit={handleSubmit(onSubmit)} data-testid='MietrechtsberatungSubmitForm'>
      <div className={styles.content}>
        <div className={styles.column}>
          {isLoading && (
            <div className={styles.loaderContainer} data-testid='loader'>
              <Loader />
            </div>
          )}
          <>
            <div className='margin-bottom-m'>
              <Msg id='benefits.rentLegal.modal.form.title' defaultMessage='Reach out to our customer service' />
            </div>
            <label className={styles.radiosLabel}>
              {t({
                id: 'benefits.rentLegal.modal.form.salutation.label',
                defaultMessage: 'Anrede',
              })}
            </label>
            <div className={styles.radios}>
              <RadioButton
                label={t({
                  id: 'benefits.rentLegal.modal.form.salutation.options.female',
                  defaultMessage: 'Frau',
                })}
                id={UserSalutation.FEMALE}
                value={UserSalutation.FEMALE}
                {...register('salutation', { required: true, validate: validateSalutation })}
                isErroneous={!!errors.salutation}
                data-testid='Frau'
              />
              <RadioButton
                label={t({
                  id: 'benefits.rentLegal.modal.form.salutation.options.male',
                  defaultMessage: 'Herr',
                })}
                id={UserSalutation.MALE}
                value={UserSalutation.MALE}
                {...register('salutation', { required: true, validate: validateSalutation })}
                isErroneous={!!errors.salutation}
                data-testid='Herr'
              />
            </div>
            <div className={styles.row}>
              <TextInput.Root id='firstName' invalid={!!errors.firstName}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.rentLegal.modal.form.firstName.label',
                    defaultMessage: 'Vorname',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  data-testid='firstName'
                  {...register('firstName', {
                    required: true,
                  })}
                  placeholder=''
                />
              </TextInput.Root>
            </div>
            <div className={styles.row}>
              <TextInput.Root id='lastName' invalid={!!errors.lastName}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.rentLegal.modal.form.lastName.label',
                    defaultMessage: 'Nachname',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  data-testid='lastName'
                  {...register('lastName', {
                    required: true,
                  })}
                  placeholder=''
                />
              </TextInput.Root>
            </div>
            <div className={classnames(styles.address1, styles.row)}>
              <div className={styles.street}>
                <TextInput.Root id='street' invalid={!!errors.street}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.rentLegal.modal.form.street.label',
                      defaultMessage: 'Straße',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    data-testid='street'
                    {...register('street', {
                      required: true,
                    })}
                    placeholder=''
                  />
                </TextInput.Root>
              </div>
              <div className={styles.houseNumber}>
                <TextInput.Root id='houseNumber' invalid={!!errors.houseNumber}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.rentLegal.modal.form.houseNumber.label',
                      defaultMessage: 'Hausnr.',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    data-testid='houseNumber'
                    {...register('houseNumber', {
                      required: true,
                    })}
                    placeholder=''
                  />
                </TextInput.Root>
              </div>
            </div>
            <div className={classnames(styles.row, styles.address2)}>
              <div className={styles.postcode}>
                <TextInput.Root id='postcode' invalid={!!errors.postcode}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.rentLegal.modal.form.postcode.label',
                      defaultMessage: 'PLZ',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    data-testid='postcode'
                    {...register('postcode', {
                      required: true,
                      pattern: /^[0-9]{5}$/,
                    })}
                    placeholder=''
                  />
                </TextInput.Root>
              </div>
              <div className={styles.city}>
                <TextInput.Root id='city' invalid={!!errors.city}>
                  <TextInput.Label>
                    {t({
                      id: 'benefits.rentLegal.modal.form.city.label',
                      defaultMessage: 'Ort',
                    })}
                  </TextInput.Label>
                  <TextInput.Input
                    data-testid='city'
                    {...register('city', {
                      required: true,
                    })}
                    placeholder=''
                  />
                </TextInput.Root>
              </div>
            </div>
          </>
          <div className={styles.disclaimer}>
            <div
              dangerouslySetInnerHTML={{
                __html: t({
                  id: 'benefits.rentLegal.modal.form.footerText',
                  defaultMessage:
                    'Ich stimme zu, dass meine Daten an die <a href="https://www.axapartners.de/" target="_blank" rel="noreferrer"> AXA Assistance </a> übermittelt und dort verarbeitet und zur Leistungserbringung genutzt werden. Ich stimme ferner zu, dass soweit dies der ordnungsgemäßen Durchführung der Versicherungsangelegenheit dient, die die AXA Assistance allgemeine Vertrags-, Abrechnungs- und Leistungsdaten in gemeinsame Datensammlungen der Versicherungsgruppe zusammenführt. Weitere Informationen zum Umgang mit Ihren Daten finden Sie <a href=https://www.axapartners.de/datenschutz" target="_blank" rel="noreferrer"> hier </a>.',
                }),
              }}
            />
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.submitFooter}>
          <Button textStyle={true} onClick={close} type='button'>
            <Msg id='benefits.rentLegal.modal.form.backButtonText' defaultMessage='Zurück' />
          </Button>
          <Button type='submit' appearance='primary' disabled={isLoading}>
            <Msg id='benefits.rentLegal.modal.form.submitButtonText' defaultMessage='Jetzt aktivieren' />
          </Button>
        </div>
      </div>
    </form>
  );
};

export default MietrechtsberatungSubmitForm;
